import React, { useMemo, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { MarkdownDisplay, TextView } from '@ygtechnologies/bygr-frontend-component-library';
import { Button, ButtonBar, Dialog } from '@ygtechnologies/rubus-ui';

import { BuyerCurrencyDisplay } from '@/buyer/components/BuyerCurrencyDisplay';
import { ThumbnailGallery, ThumbnailGalleryImage } from '@/components/ThumbnailGallery/ThumbnailGallery';
import { ConfiguratorPresetDto } from '@/services/configurator';
import { useImageUrls } from '@/components/OptionConfigurator/useImageUrls';

import { IncludedOptionsList } from './IncludedOptionsList';
import theme from './PresetDialog.module.scss';

interface Props {
    show: boolean;
    style: ConfiguratorPresetDto | null;
    deadlineId: number;
    roomId: number | null;
    onClose: () => void;
}

interface BodyProps {
    style: ConfiguratorPresetDto;
    deadlineId: number;
    roomId: number | null;
    onLightboxToggle: (open: boolean) => void;
}

const DetailsDialogBody: React.FC<BodyProps> = ({ style, deadlineId, roomId, onLightboxToggle }) => {
    const { getOptionPresetImageUrl, getOptionPresetImageThumbnailUrl } = useImageUrls();
    const images = useMemo<ThumbnailGalleryImage[]>(
        () =>
            style.images.map((img) => ({
                url: getOptionPresetImageUrl(img.id),
                thumbnailUrl: getOptionPresetImageThumbnailUrl(img.id),
            })),
        [style, getOptionPresetImageUrl, getOptionPresetImageThumbnailUrl]
    );

    return (
        <div className={theme.dialogBody}>
            <ThumbnailGallery
                useFullSizeImage
                images={images}
                className={theme.headerImageContainer}
                imageClassName={theme.headerImage}
                onLightboxToggle={onLightboxToggle}
            />
            <div className={theme.topSection}>
                <div className={theme.info}>
                    <TextView className="white-space-collapse" numberOfLines={8}>
                        <MarkdownDisplay
                            className="text-view-markdown-fixer"
                            markdown={style.description}
                        />
                    </TextView>
                </div>
                <div className={theme.priceSection}>
                    <div className={theme.priceRow}>
                        <Trans>Package price:</Trans>
                        <span className={theme.priceAmount}>
                            <BuyerCurrencyDisplay value={style.price} />
                        </span>
                    </div>
                </div>
            </div>

            <IncludedOptionsList style={style} deadlineId={deadlineId} roomId={roomId} />
        </div>
    );
};

export const PresetDetailsDialog: React.FC<Props> = ({ show, style, deadlineId, roomId, onClose }) => {
    const [lightboxOpen, setLightboxOpen] = useState(false);

    if (!show || !style) return null;

    return (
        <Dialog
            open={show}
            onOpenChange={(open) => !open && onClose()}
            closeOnClickOutside={!lightboxOpen}
            closeOnEscape={!lightboxOpen}>
            <Dialog.Content contentWidth="lg">
                <Dialog.Header>{style.name}</Dialog.Header>
                <DetailsDialogBody
                    style={style}
                    deadlineId={deadlineId}
                    roomId={roomId}
                    onLightboxToggle={setLightboxOpen}
                />
                <ButtonBar>
                    <ButtonBar.Right>
                        <Button variant="tertiary" onClick={onClose}>
                            <Trans>Close</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </Dialog.Content>
        </Dialog>
    );
};
